import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePaywall } from "@/contexts/PaywallContext";

export function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("polling");
    const [attempts, setAttempts] = useState(0);
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const { refreshStatus } = usePaywall();
    const timerRef = useRef(null);

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            return;
        }
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    await refresh();
                    await refreshStatus();
                    return;
                }
                if (data.status === "expired") {
                    setStatus("expired");
                    return;
                }
                setAttempts((a) => a + 1);
            } catch (e) {
                setAttempts((a) => a + 1);
            }
        };
        poll();
        timerRef.current = setInterval(poll, 2500);
        return () => clearInterval(timerRef.current);
    }, [sessionId, refresh, refreshStatus]);

    useEffect(() => {
        if (attempts >= 10 && status === "polling") setStatus("timeout");
        if (status === "paid" && timerRef.current) clearInterval(timerRef.current);
    }, [attempts, status]);

    return (
        <div className="min-h-screen grid place-items-center bg-background p-6 relative overflow-hidden">
            <div className="blob bg-primary" style={{ width: 320, height: 320, top: 20, right: -60 }} />
            <div className="grain absolute inset-0" />
            <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                className="relative z-10 w-full max-w-md rounded-4xl border border-border bg-card p-8 text-center"
            >
                {status === "paid" ? (
                    <>
                        <div className="mx-auto h-16 w-16 rounded-3xl bg-primary text-primary-foreground grid place-items-center">
                            <CheckCircle2 className="h-9 w-9" strokeWidth={2.5} />
                        </div>
                        <h1 className="mt-5 font-display text-3xl font-black tracking-tighter">
                            You're <span className="font-fancy text-gradient-purple">premium</span> now
                        </h1>
                        <p className="mt-2 text-muted-foreground">Unlimited swipes, unlimited chats, unlimited posts. Welcome in.</p>
                        <Button
                            className="mt-6 h-14 w-full rounded-2xl font-semibold lift"
                            onClick={() => navigate("/app/match")}
                            data-testid="payment-success-continue"
                        >
                            <Sparkles className="mr-2 h-5 w-5" /> Back to Flatch
                        </Button>
                    </>
                ) : status === "expired" ? (
                    <>
                        <div className="mx-auto h-16 w-16 rounded-3xl bg-accent text-accent-foreground grid place-items-center">
                            <XCircle className="h-9 w-9" strokeWidth={2.5} />
                        </div>
                        <h1 className="mt-5 font-display text-3xl font-black tracking-tighter">Checkout expired</h1>
                        <p className="mt-2 text-muted-foreground">Give it another go.</p>
                        <Button className="mt-6 h-14 w-full rounded-2xl font-semibold lift" onClick={() => navigate("/app/menu")}>
                            Back to menu
                        </Button>
                    </>
                ) : status === "timeout" ? (
                    <>
                        <h1 className="mt-5 font-display text-2xl font-black">Payment is taking a moment…</h1>
                        <p className="mt-2 text-muted-foreground text-sm">Stripe sometimes needs a beat. Refresh in a minute.</p>
                        <Button className="mt-6 h-14 w-full rounded-2xl font-semibold lift" onClick={() => window.location.reload()}>
                            Check again
                        </Button>
                    </>
                ) : (
                    <>
                        <div className="animate-pulse text-muted-foreground py-8" data-testid="payment-polling">
                            Confirming your subscription…
                        </div>
                    </>
                )}
            </motion.div>
        </div>
    );
}

export function PaymentCancel() {
    const navigate = useNavigate();
    return (
        <div className="min-h-screen grid place-items-center bg-background p-6 relative overflow-hidden">
            <div className="grain absolute inset-0" />
            <div className="relative z-10 w-full max-w-md rounded-4xl border border-border bg-card p-8 text-center">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-accent text-accent-foreground grid place-items-center">
                    <XCircle className="h-7 w-7" strokeWidth={2.5} />
                </div>
                <h1 className="mt-4 font-display text-2xl font-black">Checkout cancelled</h1>
                <p className="mt-2 text-muted-foreground text-sm">No charge made. You can upgrade anytime from Menu.</p>
                <Button className="mt-6 h-14 w-full rounded-2xl font-semibold lift" onClick={() => navigate("/app/menu")}>
                    Back to Flatch
                </Button>
            </div>
        </div>
    );
}
